import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, BackHandler, Platform, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';

import { updateProfileRequest } from '../../store/auth/authActions';
// Metro bundler will automatically resolve platform-specific files:
// - TutorialTable.web.tsx for web
// - TutorialTable.android.tsx for Android
// - TutorialTable.native.tsx for iOS
import TutorialTable from '../Components/Game/TutorialTable';
import BecomeVipModal from '../Components/Modals/BecomeVipModal';
import NotEnoughChipsModal from '../Components/Modals/NotEnoughChipsModal';
import { RootStackParamList } from '../navigator';

type GameScreenProps = NativeStackScreenProps<RootStackParamList, 'Tutorial'>;

const TutorialScreen: React.FC = () => {
	const route = useRoute<GameScreenProps['route']>();
	const navigation = useNavigation<GameScreenProps['navigation']>();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { tableId, roomSlug, isTutorial, isTournamentRoom } = route.params || {};
	const [showBecomeVipModal, setShowBecomeVipModal] = useState(false);
	const [showNotEnoughChipsModal, setShowNotEnoughChipsModal] = useState(false);
	const stableTimestamp = useRef((route.params && route.params.timestamp) || Date.now()).current;
	const hasNavigatedAwayRef = useRef(false);
	// Handle Android back button
	useEffect(() => {
		if (Platform.OS === 'android') {
			const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
				Alert.alert(
					t('T_SITE_TUTORIAL_EXIT_TITLE', { defaultValue: 'Exit Tutorial' }),
					t('T_SITE_TUTORIAL_EXIT_CONFIRM', {
						defaultValue: 'Are you sure you want to exit the tutorial?',
					}),
					[
						{
							text: t('T_SITE_MODAL_NEW_ROOM_CANCEL', { defaultValue: 'Cancel' }),
							style: 'cancel',
						},
						{
							text: t('T_SITE_MENU_LOGOUT', { defaultValue: 'Exit' }),
							style: 'destructive',
							onPress: () => {
								navigation.goBack();
							},
						},
					],
				);
				return true; // Prevent default back behavior
			});

			return () => backHandler.remove();
		}
	}, [navigation, t]);

	const handleGameEvent = useCallback(
		(event: string, data?: any) => {
			console.log('Game event received:', event, data);

			switch (event) {
				case 'goToLobby':
					if (hasNavigatedAwayRef.current) {
						break;
					}
					hasNavigatedAwayRef.current = true;
					navigation.replace('Lobby');
					break;

				case 'becomeVIP':
					setShowBecomeVipModal(true);
					break;

				case 'notEnoughChips':
					setShowNotEnoughChipsModal(true);
					break;

				case 'buyChips':
					navigation.navigate('BuyPackages', { activeTab: 'chips' });
					break;

				case 'setSound':
					// v1 persisted the in-game sound toggle through the user API
					if (typeof data?.setSound === 'boolean') {
						dispatch(updateProfileRequest({ isSoundEffectsEnabled: data.setSound }));
					}
					break;

				case 'openNotifications':
					// Handle notifications
					navigation.navigate('Messages');
					break;

				case 'showBlackscreenAlert':
				case 'hideBlackscreenAlert':
					// Legacy web-game black-screen heuristic: fires 5s after table load if the
					// user hasn't tapped anything. Not a real performance signal — ignored here.
					// (v1 showed T_SITE_TABLE_BSOD here; suppressing it is a deliberate v2
					// product decision from the performance MR.)
					break;

				case 'tableDoesNotExist':
					Alert.alert(t('T_SITE_TABLE_NON_EXISTING_TABLE'), undefined, [
						{ text: t('T_OK', { defaultValue: 'OK' }), onPress: () => navigation.goBack() },
					]);
					break;

				default:
					console.log('Unhandled game event:', event, data);
			}
		},
		[navigation, dispatch, t],
	);

	const handleCloseNotEnoughChipsModal = useCallback(() => {
		setShowNotEnoughChipsModal(false);
	}, []);

	const handleBuyChips = useCallback(() => {
		setShowNotEnoughChipsModal(false);
		navigation.navigate('BuyPackages', { activeTab: 'chips' });
	}, [navigation]);

	return (
		<View style={styles.container}>
			<TutorialTable
				key={`${tableId}-${stableTimestamp}`}
				tableId={tableId}
				roomSlug={roomSlug}
				isTutorial={isTutorial}
				onGameEvent={handleGameEvent}
				isTournamentRoom={isTournamentRoom}
			/>
			{showBecomeVipModal && <BecomeVipModal onClose={() => setShowBecomeVipModal(false)} />}
			<NotEnoughChipsModal
				visible={showNotEnoughChipsModal}
				onClose={handleCloseNotEnoughChipsModal}
				onBuy={handleBuyChips}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: '#000',
	},
});

export default TutorialScreen;
